import { useRef, useState } from 'react';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';

interface LogoUploaderProps {
  value?: string;
  onChange: (url: string | undefined) => void;
  /** Storage key prefix — e.g. `logos/brand` or `logos/tiles`. */
  prefix: string;
  size?: number;
  label?: string;
}

/**
 * Reusable upload-an-image control used by the home-page edit panel for the
 * SFP brand mark and per-tile logos. Stores under
 *   gs://<bucket>/logos/<prefix>/<uid>/<random>.<ext>
 * and writes the public download URL back via onChange.
 */
export function LogoUploader({
  value,
  onChange,
  prefix,
  size = 56,
  label = 'Upload logo',
}: LogoUploaderProps) {
  const { user } = useAuth();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function upload(file: File) {
    if (!user) return;
    if (!/^image\//.test(file.type)) {
      setErr('Pick a PNG, JPG, SVG, or WebP image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErr('Image too large — keep it under 2 MB.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png';
      const key = `${Math.random().toString(36).slice(2, 10)}.${ext}`;
      const objRef = storageRef(storage, `${prefix}/${user.uid}/${key}`);
      await uploadBytes(objRef, file, { contentType: file.type });
      const url = await getDownloadURL(objRef);
      onChange(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  return (
    <div className="flex items-center gap-3">
      <div
        className="grid shrink-0 place-items-center overflow-hidden rounded-lg border border-stone-200 bg-white"
        style={{ width: size, height: size }}
      >
        {value ? (
          <img src={value} alt="logo" className="h-full w-full object-cover" />
        ) : (
          <span className="text-stone-300 text-xs">—</span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={busy}
          className="rounded border border-stone-300 px-2 py-1 text-xs hover:bg-stone-100 disabled:opacity-50"
        >
          {busy ? 'Uploading…' : label}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-[10px] text-[#9C4A3A] hover:underline"
          >
            Remove
          </button>
        )}
        {err && <div className="text-[10px] text-[#9C4A3A]">{err}</div>}
      </div>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])}
      />
    </div>
  );
}
